import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";
import { translations as DEFAULTS } from "../../lib/i18n";
import { toast } from "sonner";
import { Save, RotateCcw } from "lucide-react";

const FIELDS = [
  { key: "hero.overline", label: "Hero overline (small text above title)" },
  { key: "hero.title", label: "Hero main title" },
  { key: "hero.subtitle", label: "Hero subtitle (italic line)" },
  { key: "hero.tagline", label: "Hero tagline (paragraph)" },
  { key: "hero.bookNow", label: 'Button: "Book Now"' },
  { key: "hero.viewApartments", label: 'Button: "View Apartments"' },
  { key: "hero.contactUs", label: 'Button: "Contact Us"' },
  { key: "apartments.overline", label: "Apartments section overline" },
  { key: "apartments.title", label: "Apartments section title" },
  { key: "amenities.overline", label: "Amenities overline" },
  { key: "amenities.title", label: "Amenities title" },
  { key: "gallery.overline", label: "Gallery overline" },
  { key: "gallery.title", label: "Gallery title" },
  { key: "location.overline", label: "Location overline" },
  { key: "location.title", label: "Location title" },
  { key: "location.description", label: "Location description" },
  { key: "booking.overline", label: "Booking overline" },
  { key: "booking.title", label: "Booking title" },
  { key: "booking.subtitle", label: "Booking subtitle" },
  { key: "contact.overline", label: "Contact overline" },
  { key: "contact.title", label: "Contact title" },
  { key: "contact.subtitle", label: "Contact subtitle" },
  { key: "footer.tagline", label: "Footer tagline" },
];

const getPath = (obj, path) => path.split(".").reduce((o, k) => (o ? o[k] : undefined), obj);
const setPath = (obj, path, value) => {
  const parts = path.split(".");
  const out = { ...obj };
  let cur = out;
  for (let i = 0; i < parts.length - 1; i++) {
    cur[parts[i]] = { ...(cur[parts[i]] || {}) };
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
  return out;
};

export default function TextsTab() {
  const { refresh } = useLang();
  const [overrides, setOverrides] = useState({ el: {}, en: {} });
  const [saving, setSaving] = useState(false);
  const [activeLang, setActiveLang] = useState("el");

  useEffect(() => {
    api
      .get("/translations")
      .then((r) => {
        const data = r.data || {};
        setOverrides({ el: data.el || {}, en: data.en || {} });
      })
      .catch((err) => console.error("Failed to load translations:", err));
  }, []);

  const val = (lang, key) => {
    const o = getPath(overrides[lang], key);
    if (o !== undefined && o !== "") return o;
    return getPath(DEFAULTS[lang], key) || "";
  };

  const upd = (lang, key, value) => {
    setOverrides((prev) => ({ ...prev, [lang]: setPath(prev[lang], key, value) }));
  };

  const reset = (key) => {
    upd(activeLang, key, "");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/translations", overrides);
      toast.success("Texts saved");
      if (refresh) refresh();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const input = "w-full bg-[#F5F2EB] border border-[#E9E2D3] rounded-xl px-3 py-2.5 text-[#1A2B4C] focus:outline-none focus:ring-2 focus:ring-[#1A2B4C]/30 text-sm";

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-serif text-2xl text-[#1A2B4C]">Texts Editor</h2>
        <div className="flex bg-[#F5F2EB] rounded-full p-1">
          {["el", "en"].map((lng) => (
            <button
              key={lng}
              onClick={() => setActiveLang(lng)}
              data-testid={`texts-lang-${lng}`}
              className={`px-4 py-1.5 rounded-full text-sm uppercase tracking-widest transition-all ${
                activeLang === lng ? "bg-[#1A2B4C] text-white" : "text-[#6B705C]"
              }`}
            >
              {lng}
            </button>
          ))}
        </div>
      </div>
      <p className="text-sm text-[#6B705C] mb-6">
        Edit the main copy of your website. Leave a field empty to use the default text.
      </p>

      <div className="bg-white rounded-2xl border border-[#E9E2D3] p-6 space-y-4">
        {FIELDS.map(({ key, label }) => {
          const v = getPath(overrides[activeLang], key);
          const isOverridden = v !== undefined && v !== "";
          return (
            <div key={key}>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs uppercase tracking-widest text-[#6B705C]">{label}</label>
                {isOverridden && (
                  <button
                    onClick={() => reset(key)}
                    className="text-[10px] text-[#1A2B4C] flex items-center gap-1 hover:underline"
                  >
                    <RotateCcw className="w-3 h-3" /> reset to default
                  </button>
                )}
              </div>
              {(key.includes("tagline") || key.includes("description") || key.includes("subtitle")) ? (
                <textarea
                  rows="3"
                  className={input}
                  value={val(activeLang, key)}
                  onChange={(e) => upd(activeLang, key, e.target.value)}
                />
              ) : (
                <input
                  className={input}
                  value={val(activeLang, key)}
                  onChange={(e) => upd(activeLang, key, e.target.value)}
                />
              )}
            </div>
          );
        })}

        <button
          onClick={save}
          disabled={saving}
          data-testid="save-texts"
          className="inline-flex items-center gap-2 bg-[#1A2B4C] text-white px-6 py-3 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : "Save texts"}
        </button>
      </div>
    </div>
  );
}
